import Api, { baseUrl } from '@/api'
import { useParams } from '@/hooks/useParams'
import { useUserStore } from '@/stores/user'

export type ApiResponse<T = any> = {
  code: number
  msg: string
  data: T
}
/**
 * 后端业务错误。
 * 用来兼容 catch(err) 的写法。
 */
export class ApiBusinessError extends Error {
  code: number
  data: any
  constructor(code: number, msg: string, data?: any) {
    super(msg)
    this.name = 'ApiBusinessError'
    this.code = code
    this.data = data
  }
}

type RequestOptions = {
  /** code=700 时是否抛出业务错误 */
  throwOnBusinessError?: boolean
  /** 是否跳过 public_config 前置初始化，只有 public_config 自己调用时使用 */
  skipPublicConfig?: boolean
  /** 是否携带 userid / tokens */
  includeAuth?: boolean
}

let publicConfigReady = false
let publicConfigPromise: Promise<void> | null = null

/**
 * 判断接口是否为登录失效。
 */
function isTokenInvalidCode(code: number | string | undefined | null): boolean {
  return Number(code) === 700
}

function isPublicConfigPath(path: string): boolean {
  return path === Api.publicConfig
}

/**
 * 登录失效统一处理：
 * 只清 UserID / Token，不清 UUID。
 */
function handleTokenInvalid(resp?: ApiResponse) {
  const userStore = useUserStore()
  console.warn('登录失效，清空 userid / token，保留 uuid：', resp)
  userStore.clearAuth()
}


function updateProtocolLinks(resp?: ApiResponse<any>) {
  if (Number(resp?.code) !== 1 || !resp?.data) return
  const userStore = useUserStore()
  userStore.setProtocolLinks({
    privacy: resp.data.privacy || '',
    user: resp.data.user || '',
    recharge: resp.data.recharge || '',
    rechargeexplain: resp.data.rechargeexplain || ''
  })
}

function tryParseApiResponse<T = any>(text: string): ApiResponse<T> | null {
  if (!text) return null
  try {
    const json = JSON.parse(text)
    if (json && typeof json === 'object' && 'code' in json) {
      return json as ApiResponse<T>
    }
  } catch (_) {
    // 非 JSON 文本，交给 HTTP 错误分支处理
  }
  return null
}

function handleBusinessCode<T = any>(
  resp: ApiResponse<T>,
  options?: { throwOnBusinessError?: boolean }
): ApiResponse<T> {
  if (isTokenInvalidCode(resp?.code)) {
    handleTokenInvalid(resp)
    if (options?.throwOnBusinessError !== false) {
      throw new ApiBusinessError(resp.code, resp.msg || '登录失效', resp.data)
    }
  }
  return resp
}

async function postRaw<T = any>(
  path: string,
  data: Record<string, string | number> = {},
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  const getRequestParams = useParams()
  const formData = getRequestParams({
    service: path,
    ...data
  }, {
    includeAuth: options?.includeAuth
  })
  let response: Response
  try {
    response = await fetch(baseUrl + path, {
      method: 'POST',
      body: formData
    })
  } catch (err) {
    console.error('网络请求失败：', path, err)
    throw err
  }
  const text = await response.text().catch(() => '')
  const parsedResp = tryParseApiResponse<T>(text)
  /**
   * 后端现在存在这种情况：
   * HTTP status = 502
   * body = {"code":700,"msg":"登录失效","data":{}}
   * 所以不能看到 !response.ok 就直接 throw，要先识别 body 里的业务 code。
   */
  if (!response.ok) {
    if (parsedResp && isTokenInvalidCode(parsedResp.code)) {
      console.warn('HTTP 非 2xx，但 body 是业务 700，按登录失效处理：', {
        path,
        status: response.status,
        body: parsedResp
      })
      return handleBusinessCode(parsedResp, options)
    }
    console.error('HTTP 请求失败：', {
      path,
      status: response.status,
      statusText: response.statusText,
      body: text
    })
    throw new Error(`HTTP ${response.status}: ${response.statusText}`)
  }
  if (!parsedResp) {
    console.error('接口返回不是合法 JSON：', {
      path,
      body: text
    })
    throw new Error('接口返回不是合法 JSON')
  }
  return handleBusinessCode(parsedResp, options)
}

/**
 * public_config 是游客注册/初始化接口。
 * 所有业务接口必须先调用它，否则后端不会给当前 device 建立游客身份，后续接口会一直 700。
 */
export async function ensurePublicConfig(): Promise<void> {
  if (publicConfigReady) return

  if (publicConfigPromise) {
    await publicConfigPromise
    return
  }

  publicConfigPromise = (async () => {
    console.log('开始初始化游客 public_config')
    const firstResp = await postRaw(Api.publicConfig, {}, {
      skipPublicConfig: true,
      includeAuth: false,
      throwOnBusinessError: false
    })
    updateProtocolLinks(firstResp)
    if (isTokenInvalidCode(firstResp.code)) {
      console.warn('public_config 第一次返回 700，清 token 后重试一次：', firstResp)
      handleTokenInvalid(firstResp)
      const retryResp = await postRaw(Api.publicConfig, {}, {
        skipPublicConfig: true,
        includeAuth: false,
        throwOnBusinessError: false
      })
      updateProtocolLinks(retryResp)
      if (isTokenInvalidCode(retryResp.code)) {
        throw new ApiBusinessError(retryResp.code, retryResp.msg || 'public_config 初始化失败', retryResp.data)
      }
    }
    publicConfigReady = true
    console.log('游客 public_config 初始化完成')
  })()
  try {
    await publicConfigPromise
  } finally {
    publicConfigPromise = null
  }
}

export async function myFetch<T = any>(
  path: string,
  data: Record<string, string | number> = {},
  options?: RequestOptions
): Promise<ApiResponse<T>> {
  if (!options?.skipPublicConfig && !isPublicConfigPath(path)) {
    await ensurePublicConfig()
  }
  return postRaw<T>(path, data, options)
}

/**
 * 适合游客可访问接口：
 * 如果第一次因为旧 token 返回 700，则清 token 后自动重试一次。
 */
export async function myFetchWithAuthRetry<T = any>(
  path: string,
  data: Record<string, string | number> = {}
): Promise<ApiResponse<T>> {
  const firstResp = await myFetch<T>(path, data, {
    throwOnBusinessError: false
  })
  if (isTokenInvalidCode(firstResp.code)) {
    console.warn('接口 700，已清 token，开始游客身份重试：', path)

    return await myFetch<T>(path, data, {
      throwOnBusinessError: false
    })
  }
  return firstResp
}